import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import {
  Loader2,
  Search,
  Star,
  Plus,
  Trash2,
  Package,
  Info,
} from 'lucide-react'
import { apiGet, apiPost, formatMXN } from '../lib/api'

interface RecomendadoItem {
  id: string
  sku: string
  name: string
  image: string | null
  price: number
  salePrice: number | null
  isOnSale: boolean
  stock: number
  isActive: boolean
  recommendedSort: number | null
}

interface RecomendadosResp {
  items: RecomendadoItem[]
}

interface BuscadorItem {
  id: string
  sku: string
  name: string
  image: string | null
  price: number
  salePrice: number | null
  isOnSale: boolean
  stock: number
}

interface BuscadorResp {
  total: number
  page: number
  pageSize: number
  pages: number
  items: BuscadorItem[]
}

interface PostResp {
  ok: boolean
  action: 'add' | 'remove'
  sort?: number
}

function ProductImage({
  src,
  alt,
}: {
  src: string | null
  alt: string
}) {
  if (!src) {
    return (
      <div className="grid size-12 shrink-0 place-items-center rounded-lg bg-ink-100 text-ink-400">
        <Package className="size-5" />
      </div>
    )
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="size-12 shrink-0 rounded-lg border border-ink-200 bg-white object-contain p-0.5"
    />
  )
}

function PriceCell({
  price,
  salePrice,
  isOnSale,
}: {
  price: number
  salePrice: number | null
  isOnSale: boolean
}) {
  if (isOnSale && salePrice && salePrice < price) {
    return (
      <div className="leading-tight">
        <div className="text-xs text-ink-400 line-through">{formatMXN(price)}</div>
        <div className="text-sm font-semibold text-emerald-700">
          {formatMXN(salePrice)}
        </div>
      </div>
    )
  }
  return (
    <div className="text-sm font-medium text-ink-900">{formatMXN(price)}</div>
  )
}

export function Recomendados() {
  const qc = useQueryClient()

  // ---- Lista de curados (izquierda) ----
  const recomendadosQuery = useQuery<RecomendadosResp>({
    queryKey: ['admin-recomendados'],
    queryFn: () => apiGet('/api/admin/productos-recomendados'),
  })

  // edición inline de sort_order: mantenemos un map local para no perder el
  // valor mientras el usuario escribe.
  const [sortDraft, setSortDraft] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!recomendadosQuery.data) return
    setSortDraft((prev) => {
      const next: Record<string, string> = {}
      for (const it of recomendadosQuery.data!.items) {
        // Solo (re)inicializamos un draft si no había uno ya — así no
        // pisamos lo que el usuario está tipeando.
        next[it.id] =
          prev[it.id] !== undefined
            ? prev[it.id]
            : String(it.recommendedSort ?? 0)
      }
      return next
    })
  }, [recomendadosQuery.data])

  const mutateSort = useMutation<PostResp, Error, { productId: string; sort: number }>({
    mutationFn: ({ productId, sort }) =>
      apiPost('/api/admin/productos-recomendados', {
        productId,
        action: 'add',
        sort,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-recomendados'] })
    },
  })

  const mutateRemove = useMutation<PostResp, Error, { productId: string }>({
    mutationFn: ({ productId }) =>
      apiPost('/api/admin/productos-recomendados', {
        productId,
        action: 'remove',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-recomendados'] })
      qc.invalidateQueries({ queryKey: ['admin-recomendados-buscador'] })
    },
  })

  const mutateAdd = useMutation<PostResp, Error, { productId: string; sort: number }>({
    mutationFn: ({ productId, sort }) =>
      apiPost('/api/admin/productos-recomendados', {
        productId,
        action: 'add',
        sort,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-recomendados'] })
      qc.invalidateQueries({ queryKey: ['admin-recomendados-buscador'] })
    },
  })

  function onSortBlur(productId: string) {
    const raw = sortDraft[productId]
    const parsed = Number.parseInt(raw ?? '', 10)
    if (Number.isNaN(parsed) || parsed < 0) {
      // Restablecer al valor actual si es inválido
      const current = recomendadosQuery.data?.items.find((it) => it.id === productId)
      setSortDraft((prev) => ({
        ...prev,
        [productId]: String(current?.recommendedSort ?? 0),
      }))
      return
    }
    const current = recomendadosQuery.data?.items.find((it) => it.id === productId)
    if (current && current.recommendedSort === parsed) return
    mutateSort.mutate({ productId, sort: parsed })
  }

  function onRemove(item: RecomendadoItem) {
    if (!confirm(`¿Quitar "${item.name}" de la lista de recomendados?`)) return
    mutateRemove.mutate({ productId: item.id })
  }

  // ---- Buscador (derecha) ----
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedQ(q.trim())
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [q])

  const buscadorQuery = useQuery<BuscadorResp>({
    queryKey: ['admin-recomendados-buscador', debouncedQ],
    queryFn: () =>
      apiGet(
        `/api/admin/productos-recomendados/buscador?page=1${
          debouncedQ ? `&q=${encodeURIComponent(debouncedQ)}` : ''
        }`,
      ),
    placeholderData: (prev) => prev,
  })

  const nextSort = useMemo(() => {
    const items = recomendadosQuery.data?.items ?? []
    if (items.length === 0) return 0
    const max = items.reduce(
      (acc, it) => (it.recommendedSort != null && it.recommendedSort > acc ? it.recommendedSort : acc),
      -1,
    )
    return max + 1
  }, [recomendadosQuery.data])

  function onAdd(item: BuscadorItem) {
    mutateAdd.mutate({ productId: item.id, sort: nextSort })
  }

  const curados = recomendadosQuery.data?.items ?? []

  return (
    <>
      <Topbar
        title="Productos recomendados"
        subtitle={
          recomendadosQuery.data
            ? `${curados.length.toLocaleString('es-MX')} producto(s) curado(s)`
            : 'Cargando…'
        }
      />
      <div className="p-6">
        {/* Texto explicativo */}
        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-brand-200 bg-brand-50 p-4 text-sm text-ink-700">
          <Info className="mt-0.5 size-5 shrink-0 text-brand-600" />
          <div>
            Estos productos aparecen en la sección{' '}
            <strong className="font-semibold text-ink-900">"Recomendados"</strong> del
            home. Si dejas la lista vacía, se mostrarán automáticamente los{' '}
            <strong className="font-semibold text-ink-900">12 productos más recientes con stock</strong>.
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* ----------------------- IZQUIERDA: curados ----------------------- */}
          <section className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
            <header className="flex items-center justify-between border-b border-ink-100 bg-ink-50 px-4 py-3">
              <div className="flex items-center gap-2">
                <Star className="size-4 text-brand-600" />
                <h2 className="text-sm font-semibold text-ink-900">
                  Productos recomendados (curados)
                </h2>
              </div>
              <span className="text-xs text-ink-500">
                {curados.length} {curados.length === 1 ? 'producto' : 'productos'}
              </span>
            </header>

            {recomendadosQuery.isLoading && (
              <div className="grid place-items-center py-20 text-ink-500">
                <Loader2 className="size-8 animate-spin" />
              </div>
            )}

            {!recomendadosQuery.isLoading && curados.length === 0 && (
              <div className="px-4 py-16 text-center text-sm text-ink-500">
                Aún no has marcado productos como recomendados.
                <br />
                Búscalos en el panel de la derecha y agrégalos.
              </div>
            )}

            {curados.length > 0 && (
              <ul className="divide-y divide-ink-100">
                {curados.map((it) => (
                  <li
                    key={it.id}
                    className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-ink-50"
                  >
                    <ProductImage src={it.image} alt={it.name} />
                    <div className="min-w-0 flex-1">
                      <div className="line-clamp-1 text-sm font-medium text-ink-900">
                        {it.name}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-500">
                        <span className="font-mono">{it.sku}</span>
                        {!it.isActive && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                            Inactivo
                          </span>
                        )}
                        {it.stock === 0 && (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-800">
                            Sin stock
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="hidden sm:block">
                      <PriceCell
                        price={it.price}
                        salePrice={it.salePrice}
                        isOnSale={it.isOnSale}
                      />
                    </div>
                    <div className="flex flex-col items-center gap-0.5">
                      <label className="text-[10px] uppercase tracking-wider text-ink-500">
                        Orden
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={9999}
                        value={sortDraft[it.id] ?? ''}
                        onChange={(e) =>
                          setSortDraft((prev) => ({
                            ...prev,
                            [it.id]: e.target.value,
                          }))
                        }
                        onBlur={() => onSortBlur(it.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            ;(e.target as HTMLInputElement).blur()
                          }
                        }}
                        className="w-16 rounded-md border border-ink-200 bg-white px-2 py-1 text-center text-sm outline-none focus:border-brand-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => onRemove(it)}
                      disabled={mutateRemove.isPending}
                      title="Quitar de recomendados"
                      className="inline-flex items-center gap-1 rounded-md border border-ink-200 bg-white px-2 py-1.5 text-xs font-medium text-ink-700 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                    >
                      <Trash2 className="size-3.5" />
                      <span className="hidden md:inline">Quitar</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* ----------------------- DERECHA: buscador ----------------------- */}
          <section className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
            <header className="border-b border-ink-100 bg-ink-50 px-4 py-3">
              <div className="flex items-center gap-2">
                <Plus className="size-4 text-brand-600" />
                <h2 className="text-sm font-semibold text-ink-900">Agregar producto</h2>
              </div>
              <div className="relative mt-3">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
                <input
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar por nombre o SKU…"
                  className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
                />
              </div>
              {buscadorQuery.data && (
                <div className="mt-2 text-[11px] text-ink-500">
                  {buscadorQuery.data.total.toLocaleString('es-MX')} resultado(s)
                  {buscadorQuery.data.total > buscadorQuery.data.items.length && (
                    <> · mostrando los primeros {buscadorQuery.data.items.length}</>
                  )}
                </div>
              )}
            </header>

            {buscadorQuery.isLoading && (
              <div className="grid place-items-center py-20 text-ink-500">
                <Loader2 className="size-8 animate-spin" />
              </div>
            )}

            {buscadorQuery.data && buscadorQuery.data.items.length === 0 && (
              <div className="px-4 py-16 text-center text-sm text-ink-500">
                {debouncedQ ? (
                  <>
                    No hay productos disponibles que coincidan con{' '}
                    <strong>"{debouncedQ}"</strong>.
                  </>
                ) : (
                  <>No hay productos disponibles para agregar.</>
                )}
              </div>
            )}

            {buscadorQuery.data && buscadorQuery.data.items.length > 0 && (
              <ul className="divide-y divide-ink-100">
                {buscadorQuery.data.items.map((it) => (
                  <li
                    key={it.id}
                    className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-ink-50"
                  >
                    <ProductImage src={it.image} alt={it.name} />
                    <div className="min-w-0 flex-1">
                      <div className="line-clamp-1 text-sm font-medium text-ink-900">
                        {it.name}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-500">
                        <span className="font-mono">{it.sku}</span>
                        {it.stock === 0 && (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-800">
                            Sin stock
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="hidden sm:block">
                      <PriceCell
                        price={it.price}
                        salePrice={it.salePrice}
                        isOnSale={it.isOnSale}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => onAdd(it)}
                      disabled={mutateAdd.isPending}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                    >
                      <Plus className="size-3.5" />
                      Agregar
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </>
  )
}
